import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { User, Mail, Phone, Lock, KeyRound, ArrowRight, CheckCircle2 } from 'lucide-react';

export default function Signup() {
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', password: '', otp: '' });
  const [step, setStep] = useState(1); // 1 = Details, 2 = OTP Code
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSendOtp = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Relative path for local dev & Hostinger production
      const res = await fetch('/api/auth/send-signup-otp', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: formData.email })
      });
      const data = await res.json();
      if (data.success) {
        setStep(2);
      } else {
        setError(data.message || 'Could not send OTP.');
      }
    } catch (err) {
      console.error('Send OTP Error:', err);
      setError('Server connection error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Relative path for local dev & Hostinger production
      const res = await fetch('/api/auth/verify-signup-otp', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      const data = await res.json();
      if (data.success) {
        alert('🎉 Account Verified & Created! Please Login.');
        navigate('/login');
      } else {
        setError(data.message || 'OTP verification failed.');
      }
    } catch (err) {
      console.error('Verify OTP Error:', err);
      setError('Server connection error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[85vh] flex items-center justify-center bg-[#FAF8F5] px-6 py-12 relative overflow-hidden">
      {/* Background Animated Blue Glow */}
      <div className="absolute top-1/4 right-1/3 w-96 h-96 bg-[#16325B]/10 rounded-full blur-3xl pointer-events-none" />

      <motion.div 
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.4 }}
        className="bg-white/90 backdrop-blur-xl p-8 sm:p-12 rounded-3xl border border-gray-200/80 shadow-xl max-w-md w-full space-y-6 relative z-10"
      >
        <div className="text-center space-y-2">
          <span className="text-[10px] uppercase font-extrabold tracking-widest text-[#16325B] bg-[#EEF4FA] border border-[#C7D7E8] px-3 py-1 rounded-full">
            Step {step} of 2
          </span>
          <h2 className="font-serif-title text-3xl font-extrabold text-[#0B2540]">
            {step === 1 ? 'Create Account' : 'Verify Email OTP'}
          </h2>
          <p className="text-xs text-gray-500">
            {step === 1 ? 'Join Welvate for institutional-grade wealth learning.' : `Enter 6-digit code sent to ${formData.email}`}
          </p>
        </div>

        {error && <p className="text-xs font-bold text-red-600 bg-red-50 p-3 rounded-xl border border-red-200 text-center">{error}</p>}

        {step === 1 ? (
          <form onSubmit={handleSendOtp} className="space-y-4">
            <div>
              <label className="block text-xs font-bold text-[#0B2540] uppercase tracking-wider mb-1">Full Name</label>
              <div className="relative">
                <User className="w-4 h-4 text-gray-400 absolute left-3.5 top-3.5" />
                <input 
                  type="text" 
                  required
                  placeholder="Full Name"
                  className="w-full bg-[#FAF8F5] border border-gray-200 rounded-xl py-3 pl-10 pr-4 text-xs sm:text-sm focus:outline-none focus:border-[#16325B] transition" 
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-bold text-[#0B2540] uppercase tracking-wider mb-1">Email Address</label>
              <div className="relative">
                <Mail className="w-4 h-4 text-gray-400 absolute left-3.5 top-3.5" />
                <input 
                  type="email" 
                  required
                  placeholder="you@email.com"
                  className="w-full bg-[#FAF8F5] border border-gray-200 rounded-xl py-3 pl-10 pr-4 text-xs sm:text-sm focus:outline-none focus:border-[#16325B] transition" 
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-bold text-[#0B2540] uppercase tracking-wider mb-1">Phone Number</label>
              <div className="relative">
                <Phone className="w-4 h-4 text-gray-400 absolute left-3.5 top-3.5" />
                <input 
                  type="tel" 
                  required
                  placeholder="+91 98765 43210"
                  className="w-full bg-[#FAF8F5] border border-gray-200 rounded-xl py-3 pl-10 pr-4 text-xs sm:text-sm focus:outline-none focus:border-[#16325B] transition" 
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-bold text-[#0B2540] uppercase tracking-wider mb-1">Password</label>
              <div className="relative">
                <Lock className="w-4 h-4 text-gray-400 absolute left-3.5 top-3.5" />
                <input 
                  type="password" 
                  required
                  placeholder="••••••••"
                  className="w-full bg-[#FAF8F5] border border-gray-200 rounded-xl py-3 pl-10 pr-4 text-xs sm:text-sm focus:outline-none focus:border-[#16325B] transition" 
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                />
              </div>
            </div>

            <button 
              type="submit" 
              disabled={loading}
              className="w-full bg-[#16325B] hover:bg-[#0F213D] text-white py-3.5 rounded-xl font-bold text-xs sm:text-sm transition duration-300 shadow-md disabled:opacity-50 mt-2 flex items-center justify-center gap-2"
            >
              {loading ? 'Sending OTP Code...' : 'Get Email OTP Code'}
              <ArrowRight className="w-4 h-4" />
            </button>
          </form>
        ) : (
          <form onSubmit={handleVerifyOtp} className="space-y-4">
            <div>
              <label className="block text-xs font-bold text-[#0B2540] uppercase tracking-wider mb-1">Enter 6-Digit Verification Code</label>
              <div className="relative">
                <KeyRound className="w-4 h-4 text-gray-400 absolute left-3.5 top-3.5" />
                <input 
                  type="text" 
                  required
                  maxLength="6"
                  placeholder="123456"
                  className="w-full bg-[#FAF8F5] border border-gray-200 rounded-xl py-3 pl-10 pr-4 text-center font-mono text-base font-bold tracking-widest focus:outline-none focus:border-[#16325B] transition" 
                  value={formData.otp}
                  onChange={(e) => setFormData({ ...formData, otp: e.target.value })}
                />
              </div>
            </div>

            <button 
              type="submit" 
              disabled={loading}
              className="w-full bg-[#16325B] hover:bg-[#0F213D] text-white py-3.5 rounded-xl font-bold text-xs sm:text-sm transition duration-300 shadow-md disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {loading ? 'Verifying...' : 'Verify OTP & Complete Signup'}
              <CheckCircle2 className="w-4 h-4" />
            </button>

            <button
              type="button"
              onClick={() => setStep(1)}
              className="w-full text-center text-xs font-bold text-gray-500 hover:text-[#0B2540] pt-2"
            >
              Change Email / Edit Details
            </button>
          </form>
        )}

        <p className="text-xs text-center text-gray-500 font-medium">
          Already have an account? <Link to="/login" className="text-[#16325B] font-bold hover:underline">Login</Link>
        </p>
      </motion.div>
    </div>
  );
}